<template>
    <base-dialog :value="dialog" :title="$t('common.filter_students')" :width="420" @input="onDialogInput"
        @close="hide">
        <div class="wf-body">
            <template v-if="subgroups.length">
                <div class="wf-label">{{ $t('common.subgroups') }}</div>
                <div class="wf-chips">
                    <button v-for="sg in subgroups" :key="sg.id" type="button" class="wf-chip"
                        :class="{ active: selectedSubgroups.includes(sg.id) }" @click="toggleSubgroup(sg.id)">
                        {{ sg.name }}
                    </button>
                </div>
            </template>

            <div class="wf-label">{{ $t('common.students') }}</div>
            <div class="wf-search">
                <v-icon size="15">mdi-magnify</v-icon>
                <input type="text" :placeholder="$t('common.search')" v-model="studentSearch">
            </div>
            <div class="wf-student-list">
                <label v-for="s in filteredStudents" :key="s.objectID" class="wf-student">
                    <input type="checkbox" :checked="selectedStudentIds.includes(s.objectID)"
                        @change="toggleStudent(s.objectID)">
                    <span>{{ s.name }}</span>
                </label>
                <div v-if="filteredStudents.length === 0" class="wf-empty">{{ $t('common.not_found') }}</div>
            </div>
        </div>

        <template #footer>
            <div class="filter-footer">
                <v-btn class="footer-btn footer-btn--ghost" depressed @click="reset">
                    {{ $t('common.reset') }}
                </v-btn>
                <v-btn class="footer-btn footer-btn--primary" depressed @click="applyFilter">
                    {{ $t('common.filter') }}
                </v-btn>
            </div>
        </template>
    </base-dialog>
</template>

<script>
import BaseDialog from './BaseDialog.vue';

export default {
    name: "FilterWellnessDialog",
    components: { BaseDialog },
    data() {
        return {
            dialog: false,
            subgroups: [],
            students: [],
            selectedSubgroups: [],
            selectedStudentIds: [],
            studentSearch: '',
        }
    },
    computed: {
        candidateStudents(vm = this) {
            if (!vm.selectedSubgroups.length) return vm.students
            const ids = new Set()
            vm.selectedSubgroups.forEach(sgId => {
                const sg = vm.subgroups.find(s => s.id === sgId)
                if (sg) (sg.students || []).forEach(id => ids.add(id))
            })
            return vm.students.filter(s => ids.has(s.objectID))
        },
        filteredStudents(vm = this) {
            if (!vm.studentSearch) return vm.candidateStudents
            const q = vm.studentSearch.toLowerCase()
            return vm.candidateStudents.filter(s => String(s.name || '').toLowerCase().includes(q))
        },
    },
    methods: {
        show({ subgroups = [], students = [], filter = null } = {}) {
            const vm = this
            vm.subgroups = subgroups
            vm.students = students
            vm.selectedSubgroups = filter?.subgroups ? [...filter.subgroups] : []
            vm.selectedStudentIds = filter?.studentIds ? [...filter.studentIds] : []
            vm.studentSearch = ''
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
        },
        onDialogInput(val) {
            if (!val) this.hide()
        },
        toggleSubgroup(id) {
            const vm = this
            const i = vm.selectedSubgroups.indexOf(id)
            i === -1 ? vm.selectedSubgroups.push(id) : vm.selectedSubgroups.splice(i, 1)
        },
        toggleStudent(id) {
            const vm = this
            const i = vm.selectedStudentIds.indexOf(id)
            i === -1 ? vm.selectedStudentIds.push(id) : vm.selectedStudentIds.splice(i, 1)
        },
        reset() {
            const vm = this
            vm.selectedSubgroups = []
            vm.selectedStudentIds = []
            vm.$emit('filter', null)
            vm.hide()
        },
        applyFilter() {
            const vm = this
            const hasFilter = vm.selectedSubgroups.length > 0 || vm.selectedStudentIds.length > 0
            vm.$emit('filter', hasFilter ? {
                subgroups: [...vm.selectedSubgroups],
                studentIds: [...vm.selectedStudentIds],
            } : null)
            vm.hide()
        },
    },
    watch: {
        selectedSubgroups() {
            const vm = this
            if (!vm.selectedSubgroups.length) return
            const candidateIds = new Set(vm.candidateStudents.map(s => s.objectID))
            vm.selectedStudentIds = vm.selectedStudentIds.filter(id => candidateIds.has(id))
        },
    }
}
</script>

<style scoped>
.wf-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.wf-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-txtMuted-base);
}

.wf-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
}

.wf-chip {
    padding: 5px 12px;
    border-radius: 20px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    cursor: pointer;
    transition: all 0.15s;
}

.wf-chip.active {
    background: rgba(51, 153, 153, 0.16);
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.wf-search {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 12px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    color: var(--v-txtMuted-base);
}

.wf-search input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    font-size: 13px;
    font-family: inherit;
    color: var(--v-txtPrimary-base);
}

.wf-search input::placeholder {
    color: var(--v-txtMuted-base);
}

.wf-student-list {
    display: flex;
    flex-direction: column;
    max-height: 260px;
    overflow-y: auto;
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
}

.wf-student {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    font-size: 13px;
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    border-bottom: 1px solid var(--v-outline-base);
}

.wf-student:last-child {
    border-bottom: none;
}

.wf-student:hover {
    background: var(--v-surface2-base);
}

.wf-student input {
    accent-color: var(--v-accent-base);
    width: 16px;
    height: 16px;
    cursor: pointer;
}

.wf-empty {
    padding: 16px;
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.filter-footer {
    display: flex;
}

.footer-btn.v-btn {
    height: 52px !important;
    border-radius: 0 !important;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 700;
    box-shadow: none !important;
}

.footer-btn--ghost.v-btn {
    flex: 1;
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
}

.footer-btn--primary.v-btn {
    flex: 2;
    background: var(--v-primary-base) !important;
    color: #0C0E12 !important;
}
</style>
